import React, { FC, useCallback, useState } from 'react';
import styled, { css } from 'styled-components';

import { colorStack } from '../../../styleHelpers/colors';
import { fontSizeAndHeight } from '../../../styleHelpers/fontSizes';
import { uuidv4 } from '../../../tools/authTools';

const Wrapper = styled.div<{ disabled: boolean; error?: boolean }>`
    height: 24px;
    [type="radio"] {
        position: absolute;
        left: -9999px;
    };
    [type="radio"]:checked + label,
    [type="radio"]:not(:checked) + label {
        position: relative;
        padding-left: 32px;
        cursor: pointer;
        display: flex;
        width: 100%;
        padding-top: 4px;
        flex-wrap: nowrap;
        transition: all 0.2s ease;
        white-space: nowrap;
        color: ${colorStack.content};
        ${fontSizeAndHeight[16]};
        ${({ disabled }) => disabled && css`
            color: ${colorStack.disabled};
        `};
    };
    [type="radio"]:checked + label:before {
        content: '';
        position: absolute;
        left: 0;
        top: 0;
        width: 20px;
        height: 20px;
        border-radius: 100%;
        transition: all 0.2s ease;
        border: 2px solid ${colorStack.middleBlue};
        background: ${colorStack.whiteBlue};
        ${({ disabled }) => disabled && css`
            border: 2px solid ${colorStack.disabled};
            background: ${colorStack.bodyBg};
        `};
    };
    [type="radio"]:not(:checked) + label:before {
        content: '';
        position: absolute;
        left: 0;
        top: 0;
        width: 20px;
        transition: all 0.2s ease;
        height: 20px;
        border: 2px solid ${({ error }) => error ? colorStack.middleRed : colorStack.ligthGrey};
        border-radius: 100%;
        background: ${colorStack.white};
    };
    [type="radio"]:checked + label:hover:before,
    [type="radio"]:not(:checked) + label:hover:before {
        border: 2px solid ${colorStack.middleBlue};
        ${({ disabled }) => disabled && css`
            border: 2px solid ${colorStack.ligthGrey};
        `};
    };
    [type="radio"]:checked + label:after,
    [type="radio"]:not(:checked) + label:after {
        content: '';
        width: 10px;
        height: 10px;
        background: ${colorStack.middleBlue};
        position: absolute;
        top: 7px;
        left: 7px;
        border-radius: 100%;
        transition: all 0.2s ease;
        ${({ disabled }) => disabled && css`
            background: ${colorStack.bodyBg};
        `};
    };
    [type="radio"]:not(:checked) + label:after {
        opacity: 0;
        transform: scale(0);
        ${({ disabled }) => disabled && css`
            color: ${colorStack.disabled};
        `};
    };
    [type="radio"]:checked + label:after {
        opacity: 1;
        transform: scale(1);
        ${({ disabled }) => disabled && css`
            background-color: ${colorStack.disabled};
        `};
    };
`;

const Label = styled.label`
    display: block;
    padding: 0;
    ${fontSizeAndHeight[16]};
    color: ${colorStack.content};
`;

interface IProps {
    label?: string;
    name?: string;
    value?: string | boolean;
    checked?: boolean;
    defaultChecked?: boolean;
    disabled?: boolean;
    error?: boolean;
    testingDataAttr?: string;
    onClick?(e: React.MouseEvent<HTMLButtonElement>);
}

export const RadioButton: FC<React.PropsWithChildren<IProps>> = props => {
    const [generatedId] = useState(() => uuidv4());
    const inputId = props.testingDataAttr
        ? `radio-${props.testingDataAttr}`
        : (props.name != null || props.value != null)
            ? `radio-${props.name || 'group'}-${String(props.value)}`
            : `radio-${generatedId}`;
    const isControlled = props.checked !== undefined;

    const onClickHandler = useCallback((e) => {
        if (!props.disabled) {
            props.onClick?.(e);
        }
    }, [props.onClick, props.disabled]);

    return (
        <Wrapper
            disabled={props.disabled}
            error={props.error}
            onClick={onClickHandler}
        >
            <input
                id={inputId}
                type="radio"
                name={props.name}
                value={props.value != null ? String(props.value) : undefined}
                disabled={props.disabled}
                {...(isControlled
                    ? {
                        checked: props.checked,
                        onChange: () => {
                            // Controlled by Wrapper onClick / parent state.
                        }
                    }
                    : { defaultChecked: props.defaultChecked })}
            />
            <Label
                htmlFor={inputId}
                dangerouslySetInnerHTML={{ __html: props.label ?? '' }}
            />
        </Wrapper>
    );
};
